import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Science as ResearchIcon,
  Assignment as CreateIcon,
  Pending as PendingIcon,
} from '@mui/icons-material';
import { Person, ResearchNote, ResearchType, ResearchStatus, Priority, User } from '../../../types';
import { useUser } from '../../../contexts/AuthContext';
import { ResearchNoteService } from '../../../services/researchNoteService';
import ResearchList from '../../research/ResearchList';
import ResearchEditor from '../../research/ResearchEditor';
import ResearchViewer from '../../research/ResearchViewer';

interface ResearchTabProps {
  person: Person;
}

export const ResearchTab: React.FC<ResearchTabProps> = ({ person }) => {
  const currentUser = useUser();

  // Research note state
  const [researchNotes, setResearchNotes] = useState<ResearchNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [showEditor, setShowEditor] = useState(false);
  const [showViewer, setShowViewer] = useState(false);
  const [selectedNote, setSelectedNote] = useState<ResearchNote | null>(null);

  // Filter state
  const [statusFilter, setStatusFilter] = useState<ResearchStatus | 'all'>('all');
  const [visibilityFilter, setVisibilityFilter] = useState<'all' | 'private' | 'shared'>('all');

  // Notification state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch research notes for the person
  const fetchResearchNotes = useCallback(async () => {
    if (!person?.id || !currentUser) return;

    setLoading(true);
    setError(null);

    try {
      const fetchedNotes = await ResearchNoteService.getResearchNotesByPerson(
        person.id,
        undefined,
        currentUser.uid
      );
      setResearchNotes(fetchedNotes);
    } catch (err) {
      console.error('Error fetching research notes:', err);
      setError(err instanceof Error ? err.message : 'Failed to load research notes');
    } finally {
      setLoading(false);
    }
  }, [person?.id, currentUser]);

  // Load research notes on component mount and person change
  useEffect(() => {
    fetchResearchNotes();
  }, [fetchResearchNotes]);

  // Handle research note save (create or update)
  const handleSaveResearchNote = useCallback(async (note: ResearchNote) => {
    // Add or update note in the list
    setResearchNotes(prev => {
      const exists = prev.find(n => n.id === note.id);
      if (exists) {
        return prev.map(n => n.id === note.id ? note : n);
      } else {
        return [note, ...prev];
      }
    });

    setShowEditor(false);
    setSelectedNote(null);

    // Show appropriate message
    const isPrivate = note.isPrivate;
    const isPending = !isPrivate && note.approvalStatus === 'pending';

    let message = selectedNote ? 'Research note updated successfully' : 'Research note created successfully';
    if (isPending) {
      message += ' and is awaiting approval';
    }

    setSnackbar({
      open: true,
      message,
      severity: isPending ? 'info' : 'success'
    });
  }, [selectedNote]);

  // Handle create new research note
  const handleCreateResearchNote = useCallback(() => {
    setSelectedNote(null);
    setShowEditor(true);
  }, []);

  // Handle research note click to open viewer
  const handleResearchNoteClick = useCallback((note: ResearchNote) => {
    setSelectedNote(note);
    setShowViewer(true);
  }, []);

  // Handle edit research note
  const handleEditResearchNote = useCallback((note: ResearchNote) => {
    setSelectedNote(note);
    setShowViewer(false);
    setShowEditor(true);
  }, []);

  // Handle research note deletion
  const handleDeleteResearchNote = useCallback(async (noteId: string) => {
    if (!currentUser) {
      throw new Error('You must be logged in to delete research notes');
    }

    const confirmMessage = 'Are you sure you want to delete this research note? This cannot be undone.';
    if (!window.confirm(confirmMessage)) return;

    setLoading(true);
    try {
      await ResearchNoteService.deleteResearchNote(
        noteId,
        currentUser.uid,
        currentUser.displayName,
        currentUser.role
      );

      // Remove note from state
      setResearchNotes(prev => prev.filter(n => n.id !== noteId));

      // Close viewer if this note was open
      if (selectedNote?.id === noteId) {
        setShowViewer(false);
        setSelectedNote(null);
      }

      setSnackbar({
        open: true,
        message: 'Research note deleted successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error deleting research note:', err);
      setSnackbar({
        open: true,
        message: 'Failed to delete research note',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [currentUser, selectedNote]);

  // Handle research note approval (Admin only)
  const handleApproveResearchNote = useCallback(async (noteId: string) => {
    if (!currentUser || currentUser.role !== 'admin') {
      throw new Error('Only administrators can approve research notes');
    }

    try {
      await ResearchNoteService.approveResearchNote(
        noteId,
        currentUser.uid,
        currentUser.displayName
      );

      // Refetch the updated research note
      const updatedNote = await ResearchNoteService.getResearchNoteById(noteId, currentUser.uid);
      if (!updatedNote) {
        throw new Error('Failed to retrieve updated research note');
      }

      // Update the note in state
      setResearchNotes(prev => prev.map(n =>
        n.id === noteId ? updatedNote : n
      ));

      setSnackbar({
        open: true,
        message: 'Research note approved successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error approving research note:', err);
      setSnackbar({
        open: true,
        message: 'Failed to approve research note',
        severity: 'error'
      });
    }
  }, [currentUser]);

  // Handle research note rejection (Admin only)
  const handleRejectResearchNote = useCallback(async (noteId: string, reason: string) => {
    if (!currentUser || currentUser.role !== 'admin') {
      throw new Error('Only administrators can reject research notes');
    }

    try {
      await ResearchNoteService.rejectResearchNote(
        noteId,
        reason,
        currentUser.uid,
        currentUser.displayName
      );

      // Refetch the updated research note
      const updatedNote = await ResearchNoteService.getResearchNoteById(noteId, currentUser.uid);
      if (!updatedNote) {
        throw new Error('Failed to retrieve updated research note');
      }

      // Update the note in state
      setResearchNotes(prev => prev.map(n =>
        n.id === noteId ? updatedNote : n
      ));

      setSnackbar({
        open: true,
        message: 'Research note rejected',
        severity: 'info'
      });
    } catch (err) {
      console.error('Error rejecting research note:', err);
      setSnackbar({
        open: true,
        message: 'Failed to reject research note',
        severity: 'error'
      });
    }
  }, [currentUser]);

  // Handle editor cancel
  const handleEditorCancel = useCallback(() => {
    setShowEditor(false);
    setSelectedNote(null);
  }, []);

  // Handle viewer close
  const handleViewerClose = useCallback(() => {
    setShowViewer(false);
    setSelectedNote(null);
  }, []);

  // Handle snackbar close
  const handleSnackbarClose = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // Filter research notes based on current filters
  const filteredNotes = researchNotes.filter(note => {
    // Status filter
    if (statusFilter !== 'all' && note.status !== statusFilter) {
      return false;
    }

    // Visibility filter
    if (visibilityFilter === 'private' && !note.isPrivate) {
      return false;
    }
    if (visibilityFilter === 'shared' && note.isPrivate) {
      return false;
    }

    return true;
  });

  // Count pending notes (shared notes only)
  const pendingCount = researchNotes.filter(n => !n.isPrivate && n.approvalStatus === 'pending').length;

  // Check if user can create (Editor+ can create)
  const canCreate = currentUser && ['editor', 'admin'].includes(currentUser.role);

  // Loading state
  if (loading && researchNotes.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error && researchNotes.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={fetchResearchNotes}>
          Retry
        </Button>
      </Box>
    );
  }

  // Not logged in state
  if (!currentUser) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Please log in to view and manage research notes.
        </Alert>
      </Box>
    );
  }

  // No permission state
  if (!canCreate && researchNotes.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Research notes are only available to Editors and Administrators.
        </Alert>
      </Box>
    );
  }

  // Empty state
  if (researchNotes.length === 0 && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <ResearchIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              No Research Notes Yet
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Track your genealogy research for {person.firstName}. Document objectives, findings, sources, and tasks.
            </Typography>
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<CreateIcon />}
                onClick={handleCreateResearchNote}
                size="large"
              >
                Create Research Note
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Editor Dialog */}
        {canCreate && (
          <Dialog
            open={showEditor}
            onClose={handleEditorCancel}
            maxWidth="lg"
            fullWidth
            PaperProps={{
              sx: { minHeight: '70vh' }
            }}
          >
            <ResearchEditor
              personId={person.id}
              personName={`${person.firstName} ${person.lastName}`}
              currentUser={currentUser}
              existingNote={selectedNote}
              onSave={handleSaveResearchNote}
              onCancel={handleEditorCancel}
            />
          </Dialog>
        )}

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
        >
          <Alert severity={snackbar.severity} onClose={handleSnackbarClose}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // Main research notes view
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5" fontWeight={600}>
            Research Notes ({filteredNotes.length})
          </Typography>
          {pendingCount > 0 && currentUser.role === 'admin' && (
            <Chip
              icon={<PendingIcon />}
              label={`${pendingCount} pending`}
              color="warning"
              size="small"
            />
          )}
        </Box>
        {canCreate && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateResearchNote}
            disabled={loading}
          >
            Create Research Note
          </Button>
        )}
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 150 }} size="small">
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value as ResearchStatus | 'all')}
          >
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="planning">Planning</MenuItem>
            <MenuItem value="in-progress">In Progress</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="on-hold">On Hold</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 150 }} size="small">
          <InputLabel>Visibility</InputLabel>
          <Select
            value={visibilityFilter}
            label="Visibility"
            onChange={(e) => setVisibilityFilter(e.target.value as 'all' | 'private' | 'shared')}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="private">Private</MenuItem>
            <MenuItem value="shared">Shared</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Loading overlay for operations */}
      {loading && researchNotes.length > 0 && (
        <Box sx={{ position: 'relative', mb: 2 }}>
          <Alert severity="info" sx={{ display: 'flex', alignItems: 'center' }}>
            <CircularProgress size={20} sx={{ mr: 2 }} />
            Processing research notes...
          </Alert>
        </Box>
      )}

      {/* Research List */}
      <ResearchList
        researchNotes={filteredNotes}
        loading={loading}
        currentUser={currentUser}
        onResearchNoteClick={handleResearchNoteClick}
        onEditResearchNote={handleEditResearchNote}
        onDeleteResearchNote={handleDeleteResearchNote}
        onApproveResearchNote={handleApproveResearchNote}
        onRejectResearchNote={handleRejectResearchNote}
      />

      {/* Editor Dialog */}
      {canCreate && (
        <Dialog
          open={showEditor}
          onClose={handleEditorCancel}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: { minHeight: '70vh' }
          }}
        >
          <ResearchEditor
            personId={person.id}
            personName={`${person.firstName} ${person.lastName}`}
            currentUser={currentUser}
            existingNote={selectedNote}
            onSave={handleSaveResearchNote}
            onCancel={handleEditorCancel}
          />
        </Dialog>
      )}

      {/* Research Viewer */}
      {showViewer && selectedNote && (
        <ResearchViewer
          researchNote={selectedNote}
          currentUser={currentUser}
          open={showViewer}
          onClose={handleViewerClose}
          onEdit={handleEditResearchNote}
          onDelete={handleDeleteResearchNote}
          onApprove={handleApproveResearchNote}
          onReject={handleRejectResearchNote}
        />
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert severity={snackbar.severity} onClose={handleSnackbarClose}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ResearchTab;
